import { BaseModel } from "@/models/base.model"
import type { IBase } from "@/interfaces/api/base.interface"
import { type IProductionStatement } from "@/interfaces/api/production-statement.interface"
import { Project } from "@/models/project.model"
import { ModelFactory } from "@/factories/model-factory"
import { type Moment } from "moment"
import { Sale } from "@/models/sale.model"
import { RentalDispense } from "@/models/rental-dispense.model"
import { RentalDispenseMaterialization } from "@/models/rental-dispense-materialization.model"
import type { DraftProductionStatement } from "@/models/draft-production-statement.model"
import { FileBucket } from "@/models/file-bucket.model"
import { ProductionStatementApprovalAllocation } from "@/models/production-statement-approval-allocation.model"
import type {
  IProductionStatementSnapshot,
  IProductionStatementSnapshotData
} from "@/interfaces/api/production-statement-snapshot.interface"

function relatedIds(rows: readonly (IBase | string)[]): string[] {
  return rows.flatMap((row) => {
    const id = typeof row === "string" ? row : row.id
    return id ? [id] : []
  })
}

export class ProductionStatement extends BaseModel implements IProductionStatement {
  public static className = "ProductionStatement" // Used for socket communication.
  public static verboseName = "productieverantwoording"
  public static override managedResolveFields = ["project"]

  project: Project
  fileBucket?: FileBucket
  sales: Sale[]
  rentalDispenseMaterializations: RentalDispenseMaterialization[]
  rentalDispenses: RentalDispense[]
  productionStatementApprovalAllocations: ProductionStatementApprovalAllocation[]

  periodStart?: Moment
  periodEnd?: Moment
  reference?: string
  disputedAt?: Moment | null
  comment?: string
  lastSentAt?: Moment | null
  lastRemindedAt?: Moment | null
  deletedAt: Moment | null
  deletedBy: string | null
  snapshot: IProductionStatementSnapshot | string | null

  // Annotated fields
  snapshotTurnover: string | null
  salesSum: string
  matchedAmountSum?: string
  rentalDispenseMaterializationsSum: string
  saleIds: string[]
  rentalDispenseIds: string[]
  rentalDispenseMaterializationIds: string[]

  constructor(productionStatement: IProductionStatement, managed = false) {
    super(productionStatement)

    this.project = ModelFactory.childModel(Project, productionStatement.project, managed)
    this.fileBucket = productionStatement.fileBucket
      ? ModelFactory.childModel(FileBucket, productionStatement.fileBucket, managed)
      : undefined
    this.sales = ModelFactory.childModels(Sale, productionStatement.sales, managed)
    this.rentalDispenseMaterializations = ModelFactory.childModels(
      RentalDispenseMaterialization,
      productionStatement.rentalDispenseMaterializations,
      managed
    )
    this.rentalDispenses = ModelFactory.childModels(
      RentalDispense,
      productionStatement.rentalDispenses,
      managed
    )
    this.productionStatementApprovalAllocations = ModelFactory.childModels(
      ProductionStatementApprovalAllocation,
      productionStatement.productionStatementApprovalAllocations ?? [],
      managed
    )

    this.periodStart = productionStatement.periodStart
    this.periodEnd = productionStatement.periodEnd
    this.reference = productionStatement.reference
    this.disputedAt = productionStatement.disputedAt
    this.comment = productionStatement.comment
    this.lastSentAt = productionStatement.lastSentAt
    this.lastRemindedAt = productionStatement.lastRemindedAt
    this.deletedAt = productionStatement.deletedAt ?? null
    this.deletedBy = productionStatement.deletedBy ?? null
    this.snapshot = productionStatement.snapshot ?? null
    this.snapshotTurnover = productionStatement.snapshotTurnover ?? null

    this.salesSum = productionStatement.salesSum ?? "0"
    this.matchedAmountSum = productionStatement.matchedAmountSum
    this.rentalDispenseMaterializationsSum =
      productionStatement.rentalDispenseMaterializationsSum ?? "0"
    // List responses carry IDs, while detail responses carry the rows themselves.
    // Keep the IDs in both cases so opening a saved statement can batch-load its instructions.
    this.saleIds = productionStatement.saleIds ?? relatedIds(this.sales)
    this.rentalDispenseIds =
      productionStatement.rentalDispenseIds ?? relatedIds(this.rentalDispenses)
    this.rentalDispenseMaterializationIds =
      productionStatement.rentalDispenseMaterializationIds ??
      relatedIds(this.rentalDispenseMaterializations)
  }

  /**
   * The total sum of sales and rental dispense materializations of the production statement.
   * A reverted statement's rows are released, so it shows what its snapshot recorded.
   */
  public get sum() {
    if (this.isDeleted() && this.snapshotTurnover !== null) {
      return parseFloat(this.snapshotTurnover)
    }
    return parseFloat(this.salesSum) + parseFloat(this.rentalDispenseMaterializationsSum)
  }

  public getMatchedQuantity(): number {
    if (this.isShallow() && this.matchedAmountSum !== undefined) {
      return Number(this.matchedAmountSum)
    }
    return this.productionStatementApprovalAllocations.reduce(
      (sum, allocation) => sum + Number(allocation.amount),
      0
    )
  }

  public getOutstandingQuantity(): number {
    if (this.isDeleted()) return 0 // Reverted: its rows are released, nothing is left to allocate.
    return Number((this.getQuantity() - this.getMatchedQuantity()).toFixed(8))
  }

  public isOutstanding(): boolean {
    return this.getOutstandingQuantity() > 0
  }

  public getQuantity(): number {
    return this.sum
  }

  /** Reverted (PNTS-3218): rows released, shown greyed out with its snapshot, nothing to interact with. */
  public isDeleted(): boolean {
    return this.deletedAt !== null
  }

  /** The snapshot's content when it is loaded; a shallow row only knows the snapshot's id. */
  public getSnapshotData(): IProductionStatementSnapshotData | null {
    return this.snapshot && typeof this.snapshot !== "string" ? this.snapshot.data : null
  }

  public getDisplayText(): string {
    return this.reference ? `productieverantwoording ${this.reference}` : "productieverantwoording"
  }

  public isDraft(): this is DraftProductionStatement {
    return false
  }

  /** The ISO week numbers of the production statement. */
  public get displayPeriodISOWeek(): string {
    const startWeek = this.periodStart?.isoWeek()
    const endWeek = this.periodEnd?.isoWeek()

    if (startWeek === undefined) return endWeek === undefined ? "" : `wk ${endWeek}`
    if (endWeek === undefined || endWeek === startWeek) return `wk ${startWeek}`

    return `wk ${startWeek} t/m wk ${endWeek}`
  }
}
